/**
 * @fileoverview Edit-tenant mutation hook.
 * @description Wraps PATCH /sales/tenants/:id in a TanStack mutation, invalidating every tenant
 * query (list and detail forms alike) on success.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TENANTS_QUERY_KEY } from '@/features/sales/hooks/useTenants'
import { updateTenant } from '@/features/sales/services/tenants.service'

import type { UpdateTenantRequest } from '@/features/sales/services/tenants.service'

/**
 * @description Updates a prospect's permitted profile fields and refreshes tenant queries.
 * @param {string} id - The tenant id to update.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof updateTenant>>, unknown,
 * UpdateTenantRequest>>} The mutation object (mutate with the fields to change, isPending, ...).
 */
export function useEditTenant(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: UpdateTenantRequest) => updateTenant(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
  })
}
